import type { BadgeDefinition } from '../types/gamification';
import type { Child } from '../types/child';
import type { CompletionRecord } from '../types/progress';
import type { Subject } from '../types/activity';
import badges from '../data/badges.json';

const BADGE_LIST = badges as BadgeDefinition[];

const QUIZ_TYPES = ['science-quiz', 'history-quiz', 'finance-quiz'] as const;

export function evaluateBadges(
  child: Child,
  completions: CompletionRecord[],
  todayCompletions: CompletionRecord[],
): string[] {
  const newBadges: string[] = [];

  for (const badge of BADGE_LIST) {
    if (child.badgeIds.includes(badge.id)) continue; // already earned

    const earned = checkCriteria(badge, child, completions, todayCompletions);
    if (earned) {
      newBadges.push(badge.id);
    }
  }

  return newBadges;
}

function checkCriteria(
  badge: BadgeDefinition,
  child: Child,
  completions: CompletionRecord[],
  todayCompletions: CompletionRecord[],
): boolean {
  const c = badge.criteria;

  switch (c.type) {
    case 'activities-completed': {
      const relevant = c.subject
        ? completions.filter(r => r.subject === c.subject)
        : completions;
      return relevant.length >= c.count;
    }
    case 'streak-days':
      return child.currentStreak >= c.count;
    case 'total-xp':
      return child.xp >= c.amount;
    case 'all-subjects-in-day': {
      const subjects = new Set(todayCompletions.map(r => r.subject));
      return subjects.size >= 5; // at least 5 different subjects
    }
    case 'perfect-stars': {
      const perfect = completions.filter(r => r.starsEarned === 3);
      return perfect.length >= c.count;
    }
    case 'level-reached':
      return child.level >= c.level;
    case 'all-subjects-completed': {
      const ALL_SUBJECTS: Subject[] = ['literacy', 'math', 'science', 'social-studies', 'physical', 'creative', 'coding', 'financial', 'eq'];
      const completedSubjects = new Set(completions.map(r => r.subject));
      return ALL_SUBJECTS.every(s => completedSubjects.has(s));
    }
    case 'theme-completed': {
      const themeCompletions = completions.filter(r => r.themeId === c.themeId);
      const uniqueDays = new Set(themeCompletions.map(r => r.date));
      return uniqueDays.size >= 5 && themeCompletions.length >= 15;
    }
    // Quiz-tier badges are evaluated by `evaluateQuizBadges` instead — they
    // need access to the games store, which the activity-driven evaluator
    // intentionally avoids depending on.
    case 'quiz-tier-reached':
    case 'all-quizzes-tier':
      return false;
    // Mission-completion badges are awarded directly by the missions store
    // when a mission with `unlocks.badgeId` is completed.
    case 'mission-completed':
      return false;
    default:
      return false;
  }
}

/**
 * Quiz-tier badge evaluator. Pure function — caller passes in `quizTiers`
 * (highest tier ever reached per quiz type). Returns NEW badge IDs the child
 * has just qualified for. Idempotent: skips badges already in `child.badgeIds`.
 */
export function evaluateQuizBadges(
  child: Child,
  quizTiers: Record<string, 1 | 2 | 3 | 4 | 5>,
): string[] {
  const newBadges: string[] = [];
  for (const badge of BADGE_LIST) {
    if (child.badgeIds.includes(badge.id)) continue;
    const c = badge.criteria;
    if (c.type === 'quiz-tier-reached') {
      if ((quizTiers[c.quizType] ?? 0) >= c.tier) newBadges.push(badge.id);
    } else if (c.type === 'all-quizzes-tier') {
      if (QUIZ_TYPES.every(q => (quizTiers[q] ?? 0) >= c.tier)) newBadges.push(badge.id);
    }
  }
  return newBadges;
}

export function getBadgeById(id: string): BadgeDefinition | undefined {
  return BADGE_LIST.find(b => b.id === id);
}

export function getAllBadges(): BadgeDefinition[] {
  return BADGE_LIST;
}
